import type { ReactNode } from 'react'
import SectionHeading from '../components/ui/SectionHeading'
import { useSection } from './cms-context'

/* A section's opening, read from the CMS.
 *
 * Nine of the eleven sections open the same way — a lime label, a headline
 * with an optional highlighted tail, and a paragraph — so the four fields
 * behind them are named the same everywhere and read here rather than at each
 * call site. A person who has edited one heading in the console has learned
 * how to edit all of them.
 *
 * The defaults are passed in by the section rather than fetched, so a section
 * still says the right thing with no API at all. See useSection.
 */

type CmsHeadingProps = {
  id?: string
  eyebrow?: string
  title: string
  /** The tail of the headline, set in the accent. */
  accent?: string
  lede?: string
  action?: ReactNode
  align?: 'left' | 'center'
  className?: string
}

export function CmsHeading({
  id,
  eyebrow = '',
  title,
  accent = '',
  lede = '',
  action,
  align,
  className,
}: CmsHeadingProps) {
  const cms = useSection()

  const written = cms.text('title', title)
  const highlight = cms.text('titleAccent', accent)

  return (
    <SectionHeading
      id={id}
      align={align}
      className={className}
      action={action}
      eyebrow={cms.text('eyebrow', eyebrow)}
      eyebrowProps={cms.field('eyebrow')}
      titleProps={cms.field('title')}
      ledeProps={cms.field('lede')}
      lede={cms.text('lede', lede)}
      title={
        highlight ? (
          <>
            {written} <span className="text-lime-grad">{highlight}</span>
          </>
        ) : (
          written
        )
      }
    />
  )
}
